import type {
  MatrixCheckResult,
  MatrixClassification,
  MatrixKeyEntry,
  MatrixPair,
  PartFCategoryResult,
} from "@sts/pas";

/**
 * The PAS 2035 wire shapes, transcribed from `apps/api/src/http/retrofit-routes.ts`
 * and the read models it calls in `apps/api/src/retrofit/retrofit-portal.ts`.
 *
 * Same rules as `types.ts`, and the same reason: the API publishes no schema
 * document, so these are hand-written, and the tests in `retrofit.test.tsx`
 * assert them against the real handlers running in process.
 *
 * WHY THE UNIONS BELOW ARE RE-DECLARED. `RetrofitProjectStage` and its
 * neighbours live in `apps/api/src/retrofit/retrofit-model.ts`, which is
 * application code rather than a shared package — `@sts/api` is a devDependency
 * of the portal, present for the test harness, and production code here must not
 * lean on it. That file's own header says where they should end up: "once the
 * retrofit portal needs them too they belong beside the MCS job model" in
 * `@sts/schemas`. Until somebody moves them, they are copied here, and
 * `retrofit.test.tsx` carries a type-level assertion that the two definitions
 * are identical — so a value added on the API side is a compile error here
 * rather than a screen quietly rendering an unhandled stage.
 */

export type RetrofitProjectStage =
  | "assessment-booked"
  | "assessed"
  | "plan-agreed"
  | "designed"
  | "installing"
  | "installed"
  | "lodged"
  | "handed-over"
  | "evaluated"
  | "complete"
  | "abandoned";

export type RetrofitMeasureCategory =
  | "cavity-wall-insulation"
  | "external-wall-insulation"
  | "internal-wall-insulation"
  | "loft-insulation"
  | "room-in-roof-insulation"
  | "flat-roof-insulation"
  | "pitched-roof-insulation"
  | "underfloor-insulation"
  | "park-home-insulation"
  | "draught-proofing"
  | "glazing-and-external-doors"
  | "loft-hatch"
  | "chimney-sealing"
  | "ventilation"
  | "heating-controls"
  | "boiler-replacement"
  | "heat-pump-air-source"
  | "heat-pump-ground-source"
  | "biomass-boiler"
  | "heat-network-connection"
  | "solar-photovoltaic"
  | "solar-thermal"
  | "battery-storage"
  | "shading-devices"
  | "other";

export type RetrofitMeasureStatus =
  | "existing"
  | "proposed"
  | "designed"
  | "scheduled"
  | "installed"
  | "lodged"
  | "cancelled";

export type AdfVentilationImpact = "none" | "minor" | "major";
export type AdfVentilationCategory = "a" | "b" | "c";
export type Pas2035Version = "2019" | "2023";
export type RetrofitRiskPath = "a" | "b" | "c";

export type RetrofitRole =
  | "retrofit-assessor"
  | "retrofit-coordinator"
  | "retrofit-designer"
  | "retrofit-installer"
  | "retrofit-evaluator"
  | "main-contractor";

export type RetrofitFundingScheme =
  | "warm-homes-local-grant"
  | "warm-homes-social-housing-fund"
  | "eco4"
  | "gbis"
  | "able-to-pay"
  | "other";

/* -------------------------------------------------------------------------- */
/* Projects                                                                    */
/* -------------------------------------------------------------------------- */

/** `GET /api/retrofit/projects` — `{ projects: [...] }`, most recent first. */
export interface RetrofitProjectListItem {
  readonly id: string;
  readonly reference: string;
  readonly stage: RetrofitProjectStage;
  readonly funding_scheme: string;
  readonly commenced_on: string;
  readonly job_id: string | null;
  readonly measure_count: number;
}

/**
 * The project row, returned unchanged inside `GET /api/retrofit/projects/:id`.
 *
 * Snake_case because it is a row the query returns as it stands, following the
 * same convention `Job` does.
 */
export interface RetrofitProject {
  readonly id: string;
  readonly organisation_id: string;
  /** Null on a project with no MCS job behind it — the ordinary grant case. */
  readonly job_id: string | null;
  readonly property_id: string | null;
  readonly reference: string;
  readonly commenced_on: string;
  readonly pas_version: Pas2035Version;
  readonly funding_scheme: RetrofitFundingScheme;
  readonly stage: RetrofitProjectStage;
  readonly large_scale: boolean;
  /** The withdrawn edition's A/B/C grading; null on any :2023 project. */
  readonly risk_path: RetrofitRiskPath | null;
  readonly trustmark_project_reference: string | null;
  readonly certificate_of_lodgement_reference: string | null;
  readonly lodged_at: string | null;
  readonly handed_over_on: string | null;
  readonly basic_evaluation_completed_on: string | null;
  readonly completed_at: string | null;
}

export interface LinkedJob {
  readonly id: string;
  readonly reference: string;
  readonly stage: string;
}

export interface ProjectRoleView {
  readonly id: string;
  readonly role: RetrofitRole;
  /** The holder's name where they are not a user of this system. */
  readonly holder: string | null;
  readonly trustmark_licence_number: string | null;
  readonly started_on: string;
  readonly ended_on: string | null;
}

/** `GET /api/retrofit/projects/:id`. */
export interface RetrofitProjectDetail {
  readonly project: RetrofitProject;
  /**
   * Null when there is no MCS job. Not missing data: a loft-insulation-only
   * project never touches MCS, so the screen must say so rather than showing a
   * blank field that reads as an unfilled one.
   */
  readonly job: LinkedJob | null;
  readonly roles: readonly ProjectRoleView[];
  readonly approvedDesign: {
    readonly id: string;
    readonly version: number;
    readonly approved_at: string;
  } | null;
  /** Three calendar months after handover, per 13.4.3. Null until handover. */
  readonly basicEvaluationDueOn: string | null;
}

/* -------------------------------------------------------------------------- */
/* The measures package                                                        */
/* -------------------------------------------------------------------------- */

/** A measure category that has a row on Figure D.1. */
export interface FigureD1Mapping {
  /** 1-based position on both axes of the figure. */
  readonly axis: number;
  /** The label as printed down the row axis. */
  readonly label: string;
  /** The judgement the mapping rests on, where one was needed. */
  readonly note?: string;
}

/** A measure category with no single row, and why not. */
export interface FigureD1Gap {
  readonly kind: "split" | "absent";
  readonly reason: string;
}

/** Where a measure sits on PAS 2035 Figure D.1, or why it cannot be placed. */
export type FigureD1Position = FigureD1Mapping | FigureD1Gap;

export function isOnFigureD1(
  position: FigureD1Position,
): position is FigureD1Mapping {
  return "axis" in position;
}

export interface PackageMeasureView {
  readonly id: string;
  readonly category: RetrofitMeasureCategory;
  readonly status: RetrofitMeasureStatus;
  readonly adf_ventilation_impact: AdfVentilationImpact | null;
  readonly in_adf_table_3_1: boolean;
  /** Whether this measure counted towards the ADF categorisation. */
  readonly counted: boolean;
  readonly installed_on: string | null;
  readonly mcs_job_id: string | null;
  readonly pas_2030_certificate_number: string | null;
  readonly trustmark_lodgement_reference: string | null;
  readonly figureD1: FigureD1Position;
}

/** A Figure D.1 cell, with the project's own measure categories attached. */
export interface AnnotatedMatrixPair extends MatrixPair {
  readonly categoryA: RetrofitMeasureCategory | undefined;
  readonly categoryB: RetrofitMeasureCategory | undefined;
}

export interface UncheckablePair {
  readonly category: RetrofitMeasureCategory;
  readonly reason: string;
  readonly kind: "split" | "absent";
}

export interface RecordedVentilationAssessment {
  readonly id: string;
  readonly assessed_on: string;
  readonly assessed_to: string;
  readonly ventilation_category: AdfVentilationCategory;
  readonly existing_provision_adequate: boolean;
  readonly proposed_strategy: string;
  readonly expert_advice_sought: boolean;
  readonly condensation_risk_assessed: boolean;
}

export interface VentilationCategorisation {
  readonly majorCount: number;
  readonly minorCount: number;
  readonly unclassifiedCount: number;
  readonly category: PartFCategoryResult;
  readonly isCategoryA: boolean;
  readonly condensationOrMouldObserved: boolean;
  readonly assumption: string;
  readonly recorded: RecordedVentilationAssessment | null;
}

/** The clause behind each matrix colour, as `MATRIX_CLAUSES` publishes it. */
export interface MatrixClauseRef {
  readonly standard: string;
  readonly issue: string;
  readonly clause: string;
  readonly issued: string;
  readonly url?: string;
  readonly quote?: string;
}

/** `GET /api/retrofit/projects/:id/measures`. */
export interface RetrofitMeasurePackage {
  readonly projectId: string;
  readonly measures: readonly PackageMeasureView[];
  readonly matrix: MatrixCheckResult;
  readonly pairs: readonly AnnotatedMatrixPair[];
  readonly uncheckable: readonly UncheckablePair[];
  /**
   * The verdict over the project, which is NOT `matrix.verdict`.
   *
   * A measure with no single row on Figure D.1 never reaches the matrix, so
   * `matrix.verdict` can read "compliant" over a design with an unread cell in
   * it. This field accounts for those, and is the one a screen must show.
   */
  readonly verdict: "compliant" | "non-compliant" | "indeterminate";
  readonly ventilation: VentilationCategorisation;
  readonly clauses: Readonly<Record<string, MatrixClauseRef>>;
  readonly editionWarning: string;
}

/* -------------------------------------------------------------------------- */
/* Gaps and blockers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * One gate, and whether the API would let the project past it.
 *
 * `detail` is the service's refusal verbatim — it carries the clause references
 * — and `clause` is populated only where that refusal names one. A null clause
 * is rendered as an absent authority, never as a silent omission.
 */
export interface RetrofitGate {
  readonly id: string;
  readonly requirement: string;
  readonly satisfied: boolean;
  readonly blocks: string;
  readonly detail: string | null;
  readonly failure: string | null;
  readonly clause: string | null;
}

/** `GET /api/retrofit/projects/:id/readiness`. */
export interface RetrofitReadiness {
  readonly projectId: string;
  readonly gates: readonly RetrofitGate[];
  readonly blocked: readonly RetrofitGate[];
}

export type { MatrixCheckResult, MatrixClassification, MatrixKeyEntry, MatrixPair };

/* -------------------------------------------------------------------------- */
/* TrustMark RIAPI request bodies                                              */
/*                                                                             */
/* Each shape mirrors the parsers in                                           */
/* `apps/api/src/http/retrofit-trustmark-routes.ts`, which in turn hand the    */
/* values to `apps/api/src/retrofit/retrofit-trustmark.ts`'s per-call          */
/* identity objects. Every field is a documented "refuse rather than guess"   */
/* gap that the schema does not hold today — the RIAPI request cannot be      */
/* built without them, so a caller supplies them per call. See                */
/* `retrofit-trustmark.ts` for the full reasoning on each field.               */
/* -------------------------------------------------------------------------- */

/** `AssessmentSubmit` — the organisation's TMLN plus which assessment to send. */
export interface TrustmarkAssessmentSubmitBody {
  readonly assessmentId: string;
  readonly ownerTMLN: string;
}

/** `ProjectStart` — the org identity, the scheme, and which accepted assessment to open the project against. */
export interface TrustmarkStartProjectBody {
  readonly assessmentId: string;
  readonly ownerTMLN: string;
  readonly schemeId: string;
  readonly projectType: string;
}

/** `LodgementSubmit` — organisation identity, work scheme, and RIAPI plan-stage sequencing. */
export interface TrustmarkMeasureLodgeBody {
  readonly ownerTMLN: string;
  readonly workCarriedOutBySchemeId: string;
  readonly planStageSequence: number;
}

/**
 * `ProjectComplete` / `ProjectAmend` — the post-works assessment to send, plus
 * the three RIAPI "accept" flags that the Retrofit Coordinator asserts. The
 * flags default to `false` when omitted; `retrofit-trustmark.ts` explains why
 * `submitProjectCompletion` will never set one on the caller's behalf.
 */
export interface TrustmarkCompleteProjectBody {
  readonly assessmentId: string;
  readonly acceptUprnCheck?: boolean;
  readonly acceptSamePrePostScores?: boolean;
  readonly acceptAddressCheck?: boolean;
}

/**
 * `LodgementAmend` — the RIAPI `lodgementId` for the lodgement being amended,
 * plus the amended measure objects. The `lodgementId` is caller-supplied
 * rather than looked up here: the schema retains
 * `retrofit_measures.trustmark_lodgement_reference` in a single column that
 * may hold either a `lodgementId` or a `certificateNumber`, and LodgementAmend
 * needs the former specifically.
 */
export interface TrustmarkAmendLodgementBody {
  readonly lodgementId: string;
  readonly measures: readonly Record<string, unknown>[];
}

/** `ProjectUpdatePropertyInformation` — every field optional; each holds a RIAPI taxonomy value. */
export interface TrustmarkPropertyInformationUpdate {
  readonly propertyType?: string;
  readonly propertyDetachment?: string;
  readonly propertyAge?: string;
}
